/**
 * Pagina dei permessi del set (ADR 0041), servita da `agents`.
 *
 * Una lettura unica (`GET /api/v1/agent-sets/{id}/permissions`) mette insieme persone,
 * progetti, hosting, vetrina e collegamenti di `tools`, con una `version` da rimandare
 * quando si cambia l'accesso del set a un progetto (`PATCH …/projects/{id}`). Le altre
 * modifiche della pagina passano dai client che esistevano già: collegamenti in
 * `ToolsApi`, sito e vetrina in `AgentsApi`, ruoli in `TeamApi`.
 *
 * Provvisorio dichiarato, come gli altri client: percorsi scritti a mano invece che
 * generati dagli schemi OpenAPI. `permissions.test.ts` presidia metodo, percorso e corpo.
 */

import type { ApiClient } from './client';
import type { AccessRole, MemberRole } from './team';

/** Accesso del set a un progetto. */
export type ProjectAccess = 'read' | 'write';

/** Perché un collegamento non si cambia dalla pagina; vuoto se si cambia. */
export type BindingLock = '' | 'read_only' | 'other_owner' | 'platform' | 'project';

/** Perché il sito non si pubblica né si toglie; vuoto se si può. */
export type HostingBlock = '' | 'read_only_project' | 'contributor' | 'analysis_not_approved' | 'home_project';

/** Una persona del set. */
export interface PermissionsPerson {
  /** Utente. */
  subject_id: string;
  /** Nome utente; vuoto se il set non lo conosce. */
  username: string;
  /** `owner`, `editor` o `viewer`. */
  role: 'owner' | MemberRole;
}

/** Un invito in attesa. */
export interface PermissionsInvitation {
  /** Invito. */
  id: string;
  /** Nome utente dell'invitato. */
  username: string;
  /** Ruolo offerto. */
  role: MemberRole;
  /** `pending` o `expired`. */
  status: 'pending' | 'expired';
  /** Scadenza, ISO 8601. */
  expires_at: string;
}

/** Gruppo «Persone». */
export interface PermissionsPeople {
  /** Proprietario. */
  owner: PermissionsPerson;
  /** Membri. */
  members: PermissionsPerson[];
  /** Inviti; `null` per chi non amministra il set. */
  invitations: PermissionsInvitation[] | null;
  /** Persone ammesse dal piano; `null` se senza limite o non note. */
  max_members: number | null;
  /** Membri più inviti in attesa. */
  people: number;
}

/** Un collegamento GitHub o Google di un progetto. */
export interface PermissionsBinding {
  /** Collegamento: si cambia con `PATCH /api/v1/tools/bindings/{id}`. */
  id: string;
  /** `github` o `google`. */
  provider: string;
  /** Etichetta della connessione. */
  label: string;
  /** Account della connessione. */
  account_login: string;
  /** Proprietario della connessione: `user`, `project` o `platform`. */
  owner_type: string;
  /** True se la connessione è di chi legge. */
  owned_by_me: boolean;
  /** Unico repository, o `null`. */
  repository: string | null;
  /** Accesso in `tools`. */
  access: ProjectAccess;
  /** Regole in `tools`. */
  settings: Record<string, unknown>;
  /** Accesso con cui arriva agli agenti, dopo modalità e accesso del set. */
  effective_access: ProjectAccess;
  /** Regole con cui arriva agli agenti. */
  effective_settings: Record<string, unknown>;
  /** Perché non si cambia; vuoto se si cambia. */
  locked: BindingLock;
}

/** Un progetto del set con i suoi permessi. */
export interface PermissionsProject {
  /** Progetto. */
  project_id: string;
  /** Nome. */
  name: string;
  /** Slug. */
  slug: string;
  /** True per il progetto di casa di un set di prima (accesso fisso). */
  home: boolean;
  /** Accesso del set. */
  access: ProjectAccess;
  /** Modalità (ADR 0024). */
  work_mode: 'greenfield' | 'contributor' | 'delegated';
  /** Stato dell'analisi. */
  analysis_state: 'none' | 'pending' | 'ready' | 'approved' | 'rejected';
  /** Repository. */
  repository_url: string;
  /** Sito pubblicato, se c'è. */
  hosting: { hostname: string; state: string } | null;
  /** Perché il sito non si pubblica né si toglie. */
  hosting_block: HostingBlock;
  /** Hosting esterno (ADR 0038), se c'è. */
  external_hosting: { namespace: string; allow_write: boolean; role: 'reader' | 'operator' } | null;
  /** Scelta sulla vetrina; `null` se mai fatta. */
  showcase: boolean | null;
  /** Collegamenti. */
  bindings: PermissionsBinding[];
}

/** La lettura unica. */
export interface SetPermissions {
  /** Set. */
  set_id: string;
  /** Nome del set. */
  set_name: string;
  /** Stato del set. */
  set_status: 'draft' | 'active' | 'suspended' | 'archived';
  /** True se il set è in pausa: i permessi valgono al prossimo avvio. */
  asleep: boolean;
  /** True se il set sta lavorando. */
  working: boolean;
  /** Ruolo di chi legge. */
  my_role: AccessRole;
  /** True se chi legge cambia i permessi. */
  can_edit: boolean;
  /** Versione da rimandare con il salvataggio. */
  version: string;
  /** False se i collegamenti non si sono potuti leggere. */
  bindings_available: boolean;
  /** Persone. */
  people: PermissionsPeople;
  /** Progetti. */
  projects: PermissionsProject[];
}

/** Codice del conflitto di versione. */
export const PERMISSIONS_CHANGED = 'permissions_changed';

/** Client della pagina dei permessi. */
export class PermissionsApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Tutti i permessi del set.
   *
   * @param setId Set.
   * @returns La lettura unica.
   */
  getPermissions(setId: string): Promise<SetPermissions> {
    return this.client.request<SetPermissions>('GET', `/api/v1/agent-sets/${encodeURIComponent(setId)}/permissions`);
  }

  /**
   * Cambia l'accesso del set a un progetto.
   *
   * @param setId Set.
   * @param projectId Progetto del set.
   * @param access Accesso nuovo.
   * @param version Versione letta; il server risponde 409 `permissions_changed` se è cambiata.
   * @returns La lettura dopo il cambio, con la versione nuova.
   */
  setProjectAccess(setId: string, projectId: string, access: ProjectAccess, version: string): Promise<SetPermissions> {
    return this.client.request<SetPermissions>(
      'PATCH',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/projects/${encodeURIComponent(projectId)}`,
      { access, version },
    );
  }
}
